import React, { useCallback, useEffect, useState } from 'react';

import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { extractErrorMessage } from 'utils/generic';

import {
  Loyalty,
  createLoyalty,
  deleteLoyalty,
  updateLoyalty,
  getAllLoyaltyByUser,
} from '../../services/loyaltiesService';
import TravelerLoyalty from '../components/TravelerLoyalty';

type Props = {
  onClose: () => void;
  userId: string;
};

const TravelerLoyaltyContainer = ({ onClose, userId }: Props) => {
  const [loyalties, setLoyalties] = useState<Loyalty[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();
  const { t } = useStoreTranslation();

  const fetchLoyalty = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getAllLoyaltyByUser(userId);
      setLoyalties(response);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchLoyalty();
  }, [fetchLoyalty]);

  const handleAddLoyalty = useCallback(
    async (loyalty: Loyalty) => {
      try {
        await createLoyalty(loyalty);
        await fetchLoyalty();
      } catch (error) {
        toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
      } finally {
        setIsLoading(false);
      }
    },
    [fetchLoyalty, toast, t],
  );

  const handleDeleteLoyalty = useCallback(
    async (id: string) => {
      try {
        await deleteLoyalty(id);
        await fetchLoyalty();
      } catch (error) {
        toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
      } finally {
        setIsLoading(false);
      }
    },
    [fetchLoyalty, toast, t],
  );

  const handleUpdateLoyalty = useCallback(
    async (loyalty: Loyalty, refetch = true) => {
      try {
        await updateLoyalty(loyalty);
        if (refetch) await fetchLoyalty();
      } catch (error) {
        toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
      } finally {
        setIsLoading(false);
      }
    },
    [fetchLoyalty, toast, t],
  );

  return (
    <TravelerLoyalty
      isLoading={isLoading}
      loyalties={loyalties}
      userId={userId}
      onAddLoyalty={handleAddLoyalty}
      onDeleteLoyalty={handleDeleteLoyalty}
      onUpdateLoyalty={handleUpdateLoyalty}
      onClose={onClose}
    />
  );
};

export default TravelerLoyaltyContainer;
